/**
 * SCWT-PM-V2 Phase 3 · Feb 2026 — Public PM Invite Accept page.
 *
 * Route: /pm-invite/:token
 *
 * Landlord-safe page — shows the inviting agency + local PWS Partner
 * up-front, then a small consent + password form. Backend validates
 * everything server-side and burns the token on first accept.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, Building2, MapPin, Phone, Mail, Check, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

export default function PmInviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [partner, setPartner] = useState(null);
  const [consentWording, setConsentWording] = useState("");
  const [error, setError] = useState(null);

  const [contactName, setContactName] = useState("");
  const [password, setPassword] = useState("");
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/pm-invitations/${token}`);
        if (cancelled) return;
        setInvite(data.invitation);
        setPartner(data.inviting_partner);
        setConsentWording(data.consent_wording || "");
        setContactName(data.invitation?.agency_contact_name || "");
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.data?.detail || "invitation_not_valid";
        setError(code);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const canSubmit = useMemo(() =>
    accept && contactName.trim().length >= 1 && password.length >= 8 && !submitting,
    [accept, contactName, password, submitting]
  );

  const submit = async () => {
    setSubmitting(true);
    setSubmitErr(null);
    try {
      await api.post(`/pm-invitations/${token}/accept`, {
        contact_name: contactName.trim(),
        password,
        accept_consent: accept,
      });
      setDone(true);
    } catch (e) {
      setSubmitErr(e?.response?.data?.detail || "Could not accept invitation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div data-testid="pm-invite-error" className="max-w-md w-full bg-white rounded-2xl border border-rose-100 p-8 text-center">
          <AlertTriangle className="w-8 h-8 text-rose-600 mx-auto" />
          <h1 className="text-xl font-bold mt-4 text-slate-900">Invitation not available</h1>
          <p className="text-slate-600 mt-2 text-sm">
            This invitation link is no longer valid. It may have already been
            accepted, expired, or been revoked by the sender.
          </p>
          <a href="/" className="inline-block mt-6 text-sm text-cyan-700 hover:text-cyan-900 font-semibold">
            Return to Aware Water Testing →
          </a>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div data-testid="pm-invite-success" className="max-w-md w-full bg-white rounded-2xl border border-emerald-100 p-8 text-center">
          <div className="w-14 h-14 bg-emerald-100 rounded-full mx-auto flex items-center justify-center">
            <Check className="w-7 h-7 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-bold mt-4 text-slate-900">You're in!</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Your agency account has been created. Please log in with your email
            and the password you just set.
          </p>
          <button
            data-testid="pm-invite-go-login"
            onClick={() => navigate("/login")}
            className="inline-block mt-6 bg-cyan-700 hover:bg-cyan-800 text-white font-semibold rounded-full px-6 py-2.5 text-sm"
          >
            Go to login →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <div className="text-xs text-slate-500">Property Manager invitation</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        {/* Agency + PWS Partner card — the "who's inviting me" answer */}
        <section data-testid="pm-invite-participants" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
            <ShieldCheck className="w-3.5 h-3.5" /> Aware Water Testing · Property Manager Programme
          </div>
          <h1 data-testid="pm-invite-h1" className="mt-3 text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
            Hi {invite?.agency_contact_name || "there"}, <span data-testid="pm-invite-partner">{partner?.business_name || "Your local PWS Partner"}</span> has
            invited <span data-testid="pm-invite-agency">{invite?.agency_name}</span> to join Aware.
          </h1>
          <p className="text-slate-600 mt-3 text-sm sm:text-base leading-relaxed">
            Aware Water Testing is the primary platform. Your agency and the
            local PWS Partner appear alongside it as participating partners.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Agency</div>
              </div>
              <div className="mt-2 font-semibold text-slate-900">{invite?.agency_name}</div>
              <div className="text-xs text-slate-600 mt-0.5">{invite?.agency_email}</div>
            </div>
            <div className="rounded-xl bg-cyan-50 border border-cyan-100 p-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-cyan-700" />
                <div className="text-xs font-semibold uppercase tracking-wider text-cyan-700">Local PWS Partner</div>
              </div>
              <div className="mt-2 font-semibold text-slate-900">{partner?.business_name || "—"}</div>
              {partner?.region && <div className="text-xs text-slate-600 mt-0.5">{partner.region}</div>}
              <div className="flex flex-wrap gap-3 text-xs text-slate-600 mt-2">
                {partner?.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{partner.email}</span>}
                {partner?.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{partner.phone}</span>}
              </div>
            </div>
          </div>
        </section>

        {/* Consent + password form */}
        <section data-testid="pm-invite-form" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="text-lg font-bold text-slate-900">Set up your login</h2>
          <p className="text-slate-600 text-sm mt-1">
            Free onboarding — no subscription, no upfront charge. You can add
            properties, landlords and tenants at your own pace.
          </p>

          <div className="mt-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Your name</label>
              <input
                data-testid="pm-invite-name"
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Jane Manager"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Choose a password (min 8 characters)</label>
              <input
                data-testid="pm-invite-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="At least 8 characters"
                autoComplete="new-password"
              />
            </div>
          </div>

          {consentWording && (
            <div data-testid="pm-invite-consent-wording" className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs sm:text-sm text-slate-700 whitespace-pre-line leading-relaxed">
              {consentWording}
            </div>
          )}

          <label className="mt-4 flex gap-3 items-start text-sm text-slate-700 cursor-pointer">
            <input
              data-testid="pm-invite-consent"
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
            />
            <span>I agree to the above and want to activate our agency account.</span>
          </label>

          {submitErr && (
            <div data-testid="pm-invite-submit-error" className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
              {submitErr}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              data-testid="pm-invite-submit"
              onClick={submit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 disabled:bg-slate-300 text-white font-semibold rounded-full px-6 py-2.5 text-sm"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Accept invitation & set password
            </button>
            <div className="text-xs text-slate-500">Expires {invite?.expires_at?.slice(0, 10)}</div>
          </div>
        </section>
      </main>
    </div>
  );
}
